import React from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from '@mui/material';
import {NAVY, PageHeader, BandChip, GuidanceBanner, headCell, bodyCell} from '../components/widgets.comp';
import {PARTICIPATION_BAND_COLORS} from '../../../utils/constants';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {useDashboard} from '../../../context/DashboardContext';
import {findCard, whole, benchmarkOf} from '../networkData';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

const GREY = '#9CA3AF';
const confidenceColor = (band) => PARTICIPATION_BAND_COLORS[band] || GREY;

/* Slide 30 — "Participation by Region", renamed "Participation by School"
   per the deck's "→ school" note (SND-30-01, SND-30-02).

     SND-30-01  rate bars + threshold   SND.S_9.C_6   BarChartBMCard
                (rate %, confidence band, "50% threshold" benchmark)
     SND-30-02  school table            SND.S_9.C_7   TableCard "Coverage by School"
                (School · Participants · Registered · rate)

   The threshold marker is the card's own "50% threshold" benchmark. Every
   column of the table sorts from its header; filters appear once the network
   has enough schools to filter (QA 9 Oct 2026). */
const ParticipationBar = ({school, rate, band, threshold}) => (
    <Box sx={{mb: 2.5}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75}}>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY}}>{school}</Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY}}>{rate}%</Typography>
                {band && <BandChip label={band} color={confidenceColor(band)}/>}
            </Box>
        </Box>
        <Box sx={{position: 'relative', height: 20, borderRadius: '10px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'}}>
            <Tooltip title={`${rate}%`} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(rate || 0, 100)}%`, backgroundColor: confidenceColor(band), borderRadius: '10px'}}/>
            </Tooltip>
            {threshold !== null && (
            <Tooltip title={`Threshold: ${threshold}%`} arrow placement="top">
                <Box sx={{
                    position: 'absolute', left: `${threshold}%`, top: '-4px', width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '8px solid #374151',
                    transform: 'translateX(-50%)',
                }}/>
            </Tooltip>
            )}
        </Box>
    </Box>
);

const SCHOOL_COLUMNS = [
    {key: 'school', label: 'School', type: 'text'},
    {key: 'participants', label: 'Participants', type: 'number'},
    {key: 'registered', label: 'Registered', type: 'number'},
    {key: 'rate', label: 'Rate', type: 'percent'},
];

// The table sits in its own component so the sort/filter hook runs below the page's loading branches.
const SchoolParticipationTable = ({rows}) => {
    const t = useTableControls(rows, SCHOOL_COLUMNS);
    return (
        <>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t} sx={{px: 3}}/>}
            <TableContainer>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <SortFilterHeaderCell {...t.headerProps('school')} sx={headCell}/>
                            <SortFilterHeaderCell {...t.headerProps('participants')} sx={{...headCell, textAlign: 'center'}}/>
                            <SortFilterHeaderCell {...t.headerProps('registered')} sx={{...headCell, textAlign: 'center'}}/>
                            <SortFilterHeaderCell {...t.headerProps('rate')} sx={{...headCell, textAlign: 'center'}}/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length > 0 && t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>No school matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((row) => (
                            <TableRow key={row.school}>
                                <TableCell sx={{...bodyCell, fontWeight: 700, color: NAVY}}>{row.school}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.participants ?? '—'}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>{row.registered ?? '—'}</TableCell>
                                <TableCell sx={{...bodyCell, textAlign: 'center'}}>
                                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5}}>
                                        <Typography sx={{fontSize: '13px', fontWeight: 700}}>{row.rate === null ? '—' : `${row.rate}%`}</Typography>
                                        {row.band && <BandChip label={row.band} color={confidenceColor(row.band)}/>}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    );
};

const PARTICIPATION_SECTION_CODE = 'S_9';
const CARD_SECTIONS = [PARTICIPATION_SECTION_CODE];

const ParticipationBySchoolTab = () => {
    const {dashboardData, loading} = useDashboard({ card_sections: CARD_SECTIONS });

    if (loading) {
        return <CircularLoader />;
    }

    if (!dashboardData) {
        return <NoDataAvailable />;
    }
    const barCard = findCard(dashboardData, PARTICIPATION_SECTION_CODE, 'C_6');
    const BARS = (barCard?.items || []).map((item) => ({
        school: item.label,
        rate: whole(item.value),
        band: item.band,
        threshold: benchmarkOf(item, '50% threshold'),
    }));
    const THRESHOLD_PCT = BARS.find((b) => b.threshold !== null)?.threshold ?? null;

    const cols = findCard(dashboardData, PARTICIPATION_SECTION_CODE, 'C_7')?.table_objects || [];
    const col = (re) => cols.find((c) => re.test(c.column_name));
    const schoolCol = col(/school/i);
    const participantsCol = col(/participant/i);
    const registeredCol = col(/registered/i);
    const rateCol = col(/rate/i);
    const SCHOOL_ROWS = (schoolCol?.values || []).map((school, i) => {
        const info = participantsCol?.cell_data?.[i] || {};
        return {
            school,
            participants: whole(participantsCol?.values?.[i]),
            registered: whole(registeredCol?.values?.[i]),
            rate: rateCol ? whole(rateCol.values?.[i]) : whole(info.rate),
            band: rateCol?.cell_data?.[i]?.band || info.band,
        };
    });

    return (
    <Box>
        <PageHeader
            title="Participation by School"
            subtitle="Which schools have not participated enough for us to trust school comparisons?"
        />

        <Box sx={{display: 'flex', gap: 2.5, alignItems: 'stretch', flexDirection: {xs: 'column', md: 'row'}, mb: 3}}>
            <Box sx={{flex: 1, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2.5}}>
                    {barCard?.title || 'Participation by School'}{THRESHOLD_PCT !== null ? ` · ${THRESHOLD_PCT}% threshold` : ''}
                </Typography>
                {BARS.map((row) => <ParticipationBar key={row.school} {...row}/>)}
            </Box>

            <Box sx={{flex: 1, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden'}}>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, p: 3, pb: 2}}>
                    Participation by School
                </Typography>
                <SchoolParticipationTable rows={SCHOOL_ROWS}/>
            </Box>
        </Box>

        <GuidanceBanner
            label="Recommended action:"
            text={[
                'Contact school leads below 50% participation before finalizing school comparisons.',
                'Flag low-participation schools in heatmaps and trend charts.',
                'If response volume remains low, aggregate schools or suppress comparisons.',
            ]}
        />
    </Box>
    );
};

export default ParticipationBySchoolTab;
